import type { FC } from "react";
import { useState } from "react";
import { Box, DialogActions, Link, Stack, Typography, DialogContent, DialogTitle, Button } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import { Translate, useTranslation } from "@privacy/translations";
import type { UnansweredMandatoryQuestionsError } from "../api/registrations.types";
import { STATUS_WORDS } from "./StatusWord";

type Question = { questionId: string; header: string };

const focusQuestion = (questionId: string) =>
  requestAnimationFrame(() => {
    const input = document.getElementById(questionId);
    input?.scrollIntoView({ block: "center", behavior: "smooth" });
    input?.focus({ preventScroll: true });
  });

export const UnansweredMandatoryQuestionsDialog: FC<{
  onClose: () => void;
  sections: UnansweredMandatoryQuestionsError["unansweredMandatoryQuestionSections"];
}> = ({ onClose, sections }) => {
  const { t } = useTranslation();
  const filledSections = sections.filter(section => section.unansweredMandatoryQuestions.length > 0);
  const questions = filledSections.flatMap(section => section.unansweredMandatoryQuestions);
  const [reachable] = useState(
    () => new Set(questions.map(question => question.questionId).filter(id => document.getElementById(id)))
  );
  const goTo = (questionId: string) => {
    onClose();
    focusQuestion(questionId);
  };

  return (
    <Dialog open={true} onClose={onClose} disableRestoreFocus>
      <DialogTitle>{t("RegistrationDrawer.MissingDetailsDialog.Title")}</DialogTitle>
      <DialogContent>
        <Typography
          component="div"
          sx={{ fontSize: theme => theme.typography.pxToRem(14), color: "text.secondary", mb: 2 }}
        >
          <Translate
            translationKey={
              questions.length > 1
                ? "RegistrationDrawer.MissingDetailsDialog.Intro.Plural"
                : "RegistrationDrawer.MissingDetailsDialog.Intro.Singular"
            }
            values={STATUS_WORDS}
          />
        </Typography>
        <Stack sx={{ gap: 2.5 }}>
          {filledSections.map(section => (
            <MissingDetailsGroup
              key={section.sectionName}
              label={section.sectionName}
              questions={section.unansweredMandatoryQuestions}
              reachable={reachable}
              onGoTo={goTo}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("RegistrationDrawer.UnansweredMandatoryQuestionsDialog.Button")}</Button>
      </DialogActions>
    </Dialog>
  );
};

const MissingDetailsGroup: FC<{
  label: string;
  questions: Array<Question>;
  reachable: Set<string>;
  onGoTo: (questionId: string) => void;
}> = ({ label, questions, reachable, onGoTo }) => (
  <Box>
    <Typography
      sx={{
        fontSize: theme => theme.typography.pxToRem(13),
        fontWeight: 600,
        color: "text.secondary",
        mb: 1,
      }}
    >
      {label}
    </Typography>
    <Stack component="ul" sx={{ m: 0, p: 0, listStyle: "none", gap: 1 }}>
      {questions.map(({ questionId, header }) => (
        <Typography component="li" key={questionId} sx={{ fontSize: theme => theme.typography.pxToRem(14) }}>
          {reachable.has(questionId) ? (
            <Link
              component="button"
              type="button"
              onClick={() => onGoTo(questionId)}
              underline="hover"
              sx={{ font: "inherit", fontWeight: 400, verticalAlign: "baseline" }}
            >
              {header}
            </Link>
          ) : (
            header
          )}
        </Typography>
      ))}
    </Stack>
  </Box>
);
